/**
 * Mixpanel Analytics Service
 * Provides type-safe event tracking with React integration
 */

import mixpanel, { Dict, Mixpanel } from 'mixpanel-browser';
import type {
  MixpanelEventName,
  MixpanelEventMap,
  MixpanelUserProperties,
  MixpanelSuperProperties,
  AppEnvironment,
  MixpanelConfig,
} from './types';

class MixpanelService {
  private initialized = false;
  private mixpanelInstance: Mixpanel | null = null;
  private environment: AppEnvironment = 'development';

  /**
   * Initialize Mixpanel SDK
   */
  init(config: MixpanelConfig): void {
    if (this.initialized) {
      console.warn('[Mixpanel] Already initialized');
      return;
    }

    if (!config.token) {
      console.error('[Mixpanel] Token is required for initialization');
      return;
    }

    try {
      // Initialize Mixpanel with optimal configuration
      mixpanel.init(config.token, {
        debug: config.debug ?? false,
        track_pageview: config.track_pageview ?? false, // We'll track manually for better control
        persistence: (config.persistence ?? 'localStorage') as any,
        secure_cookie: config.secure_cookie ?? true,
        ip: config.ip ?? false, // Privacy-friendly: don't track IP
        ignore_dnt: false, // Respect Do Not Track
        loaded: (mp) => {
          this.mixpanelInstance = mp;
          if (config.loaded) {
            config.loaded(mp);
          }
        },
        // EU/India data residency support
        ...(config.api_host && { api_host: config.api_host }),
      });

      this.initialized = true;
      console.log('[Mixpanel] Initialized successfully');
    } catch (error) {
      console.error('[Mixpanel] Initialization failed:', error);
    }
  }

  /**
   * Check if Mixpanel is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Set environment (development, qa, production)
   */
  setEnvironment(env: AppEnvironment): void {
    this.environment = env;
    this.registerSuperProperty('environment', env);
  }

  /**
   * Get current environment
   */
  getEnvironment(): AppEnvironment {
    return this.environment;
  }

  /**
   * Track an event with type-safe properties
   */
  track<T extends MixpanelEventName>(
    eventName: T,
    properties?: MixpanelEventMap[T]
  ): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. Event not tracked:', eventName);
      return;
    }

    try {
      const enrichedProperties = {
        ...properties,
        environment: this.environment,
        timestamp: new Date().toISOString(),
      };

      mixpanel.track(eventName, enrichedProperties as Dict);

      if (this.environment === 'development') {
        console.log('[Mixpanel] Event tracked:', eventName, enrichedProperties);
      }
    } catch (error) {
      console.error('[Mixpanel] Error tracking event:', eventName, error);
    }
  }

  /**
   * Identify a user
   */
  identify(userId: string | number): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. User not identified');
      return;
    }

    try {
      mixpanel.identify(String(userId));
      console.log('[Mixpanel] User identified:', userId);
    } catch (error) {
      console.error('[Mixpanel] Error identifying user:', error);
    }
  }

  /**
   * Set user profile properties
   */
  setUserProperties(properties: MixpanelUserProperties): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. User properties not set');
      return;
    }

    try {
      mixpanel.people.set(properties as Dict);

      if (this.environment === 'development') {
        console.log('[Mixpanel] User properties set:', properties);
      }
    } catch (error) {
      console.error('[Mixpanel] Error setting user properties:', error);
    }
  }

  /**
   * Increment a user property (useful for counters like invoices_created)
   */
  incrementUserProperty(property: string, by: number = 1): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. User property not incremented');
      return;
    }

    try {
      mixpanel.people.increment(property, by);

      if (this.environment === 'development') {
        console.log('[Mixpanel] User property incremented:', property, by);
      }
    } catch (error) {
      console.error('[Mixpanel] Error incrementing user property:', error);
    }
  }

  /**
   * Set user property only once (useful for created_at, first_login, etc.)
   */
  setUserPropertyOnce(properties: Partial<MixpanelUserProperties>): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. User property not set');
      return;
    }

    try {
      mixpanel.people.set_once(properties as Dict);
    } catch (error) {
      console.error('[Mixpanel] Error setting user property once:', error);
    }
  }

  /**
   * Register super properties (sent with every event)
   */
  registerSuperProperties(properties: Partial<MixpanelSuperProperties>): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. Super properties not registered');
      return;
    }

    try {
      mixpanel.register(properties as Dict);

      if (this.environment === 'development') {
        console.log('[Mixpanel] Super properties registered:', properties);
      }
    } catch (error) {
      console.error('[Mixpanel] Error registering super properties:', error);
    }
  }

  /**
   * Register a single super property
   */
  registerSuperProperty(key: string, value: any): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. Super property not registered');
      return;
    }

    try {
      mixpanel.register({ [key]: value });
    } catch (error) {
      console.error('[Mixpanel] Error registering super property:', error);
    }
  }

  /**
   * Unregister a super property
   */
  unregisterSuperProperty(propertyName: string): void {
    if (!this.initialized) return;

    try {
      mixpanel.unregister(propertyName);
    } catch (error) {
      console.error('[Mixpanel] Error unregistering super property:', error);
    }
  }

  /**
   * Reset user data (call on logout)
   */
  reset(): void {
    if (!this.initialized) {
      console.warn('[Mixpanel] Not initialized. Reset skipped');
      return;
    }

    try {
      mixpanel.reset();
      console.log('[Mixpanel] User data reset');
    } catch (error) {
      console.error('[Mixpanel] Error resetting:', error);
    }
  }

  /**
   * Opt user out of tracking
   */
  optOut(): void {
    if (!this.initialized) return;

    try {
      mixpanel.opt_out_tracking();
      console.log('[Mixpanel] User opted out');
    } catch (error) {
      console.error('[Mixpanel] Error opting out:', error);
    }
  }

  /**
   * Opt user in to tracking
   */
  optIn(): void {
    if (!this.initialized) return;

    try {
      mixpanel.opt_in_tracking();
      console.log('[Mixpanel] User opted in');
    } catch (error) {
      console.error('[Mixpanel] Error opting in:', error);
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName?: string): void {
    if (!this.initialized) return;

    try {
      const properties = {
        page: pageName || window.location.pathname,
        url: window.location.href,
        referrer: document.referrer,
        environment: this.environment,
      };

      mixpanel.track('page_viewed', properties);

      if (this.environment === 'development') {
        console.log('[Mixpanel] Page view tracked:', properties);
      }
    } catch (error) {
      console.error('[Mixpanel] Error tracking page view:', error);
    }
  }

  /**
   * Time an event (start)
   */
  timeEvent(eventName: string): void {
    if (!this.initialized) return;

    try {
      mixpanel.time_event(eventName);
    } catch (error) {
      console.error('[Mixpanel] Error timing event:', error);
    }
  }

  /**
   * Get distinct ID
   */
  getDistinctId(): string | undefined {
    if (!this.initialized) return undefined;

    try {
      return mixpanel.get_distinct_id();
    } catch (error) {
      console.error('[Mixpanel] Error getting distinct ID:', error);
      return undefined;
    }
  }

  /**
   * Alias user (link anonymous user to identified user)
   */
  alias(alias: string): void {
    if (!this.initialized) return;

    try {
      mixpanel.alias(alias);
      console.log('[Mixpanel] User aliased:', alias);
    } catch (error) {
      console.error('[Mixpanel] Error aliasing user:', error);
    }
  }

  /**
   * Get Mixpanel instance (for advanced usage)
   */
  getInstance(): Mixpanel | null {
    return this.mixpanelInstance;
  }
}

// Export singleton instance
export const mixpanelService = new MixpanelService();
export default mixpanelService;
